/**
 * Helper del Controllo di Gestione: servizi, formattatori, periodi.
 */

export const SERVIZI = [
  { key: 'pranzo_lavoro', label: 'Pranzo lavoro', short: 'Pranzo', color: '#D97706' },
  { key: 'cena', label: 'Cena', short: 'Cena', color: '#D94F3D' },
  { key: 'domenica_pranzo', label: 'Domenica pranzo', short: 'Domenica', color: '#15803D' },
  { key: 'eventi', label: 'Eventi/fuori', short: 'Eventi', color: '#57534E' },
];

export const SERVIZI_FILTRO = [
  { key: 'tutti', label: 'Tutti i servizi' },
  ...SERVIZI.map((s) => ({ key: s.key, label: s.label })),
];

export const MACRO_COLORS = {
  'Pizza Bruna': '#D94F3D',
  Carta: '#B45309',
  'Menù pranzo': '#0E7490',
  'Carta domenica': '#15803D',
  Fritti: '#D97706',
  Dolci: '#A21CAF',
  Beverage: '#1D4ED8',
  Coperto: '#78716C',
  Altro: '#A8A29E',
};

export const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

export const GRANULARITA = [
  { value: 'giorno', label: 'Giorno' },
  { value: 'mese', label: 'Mese' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'semestre', label: 'Semestre' },
  { value: 'anno', label: 'Anno' },
  { value: 'tutto', label: "Tutto l'archivio" },
];

const pad = (n) => String(n).padStart(2, '0');
const ultimoGiorno = (anno, mese) => new Date(anno, mese, 0).getDate();

export const oggiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const formatData = (iso) => {
  if (!iso) return '—';
  const [a, m, g] = iso.split('-');
  return `${Number(g)} ${MESI[Number(m) - 1]?.slice(0, 3).toLowerCase()} ${a}`;
};

/** Calcola l'intervallo di date dalla selezione di granularità. */
export function periodoRange(sel, archivio) {
  const arcMin = archivio?.data_min || '2020-01-01';
  const arcMax = archivio?.data_max || oggiIso();

  if (!sel || sel.granularita === 'tutto') {
    return { data_inizio: arcMin, data_fine: arcMax, label: "Tutto l'archivio" };
  }
  const anno = Number(sel.anno) || Number(arcMax.slice(0, 4));

  switch (sel.granularita) {
    case 'giorno': {
      const g = sel.giorno || arcMax;
      return { data_inizio: g, data_fine: g, label: formatData(g) };
    }
    case 'mese': {
      const m = Number(sel.mese) || 1;
      return {
        data_inizio: `${anno}-${pad(m)}-01`,
        data_fine: `${anno}-${pad(m)}-${pad(ultimoGiorno(anno, m))}`,
        label: `${MESI[m - 1]} ${anno}`,
      };
    }
    case 'trimestre': {
      const t = Number(sel.trimestre) || 1;
      const mi = (t - 1) * 3 + 1;
      const mf = mi + 2;
      return {
        data_inizio: `${anno}-${pad(mi)}-01`,
        data_fine: `${anno}-${pad(mf)}-${pad(ultimoGiorno(anno, mf))}`,
        label: `${t}° trimestre ${anno}`,
      };
    }
    case 'semestre': {
      const s = Number(sel.semestre) || 1;
      const mi = s === 1 ? 1 : 7;
      const mf = s === 1 ? 6 : 12;
      return {
        data_inizio: `${anno}-${pad(mi)}-01`,
        data_fine: `${anno}-${pad(mf)}-${pad(ultimoGiorno(anno, mf))}`,
        label: `${s}° semestre ${anno}`,
      };
    }
    case 'anno':
    default:
      return {
        data_inizio: `${anno}-01-01`,
        data_fine: `${anno}-12-31`,
        label: `Anno ${anno}`,
      };
  }
}

/** Selezione di default: il mese più recente presente in archivio. */
export function defaultSelezione(archivio) {
  const ref = archivio?.data_max || oggiIso();
  return {
    granularita: 'mese',
    anno: Number(ref.slice(0, 4)),
    mese: Number(ref.slice(5, 7)),
    trimestre: Math.ceil(Number(ref.slice(5, 7)) / 3),
    semestre: Number(ref.slice(5, 7)) <= 6 ? 1 : 2,
    giorno: ref,
  };
}

/** Stessa granularità, anno precedente (default per il confronto). */
export function selezionePrecedente(sel) {
  if (!sel) return sel;
  if (sel.granularita === 'giorno') {
    const [a, m, g] = sel.giorno.split('-');
    return { ...sel, giorno: `${Number(a) - 1}-${m}-${g}`, anno: Number(a) - 1 };
  }
  return { ...sel, anno: Number(sel.anno) - 1 };
}

export const anniDisponibili = (archivio) => {
  const anni = archivio?.anni?.length ? archivio.anni.map(Number) : [];
  const corrente = new Date().getFullYear();
  if (!anni.includes(corrente)) anni.push(corrente);
  return [...new Set(anni)].sort((a, b) => b - a);
};

// ============== FORMATTATORI ==============
export const fmtEuro = (v, dec = 0) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(v || 0);

export const fmtNum = (v, dec = 0) =>
  new Intl.NumberFormat('it-IT', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(v || 0);

export const fmtPct = (v, dec = 1) => `${fmtNum((v || 0) * 100, dec)}%`;

/** Formatta una metrica in base al suo tipo (euro / percento / numero / decimale). */
export const fmtMetrica = (valore, tipo) => {
  switch (tipo) {
    case 'euro':
      // importi piccoli (spesa per coperto, scontrino medio) con i centesimi
      return fmtEuro(valore, Math.abs(valore || 0) < 1000 ? 2 : 0);
    case 'percento':
      return fmtPct(valore, 1);
    case 'decimale':
      return fmtNum(valore, 1);
    default:
      return fmtNum(valore, 0);
  }
};

/** Delta formattato: % per euro e numeri, punti percentuali per le percentuali. */
export const fmtDelta = (riga) => {
  if (riga.tipo === 'percento') {
    const v = riga.delta_punti;
    if (v === null || v === undefined) return '—';
    return `${v > 0 ? '+' : ''}${fmtNum(v, 1)} pp`;
  }
  if (riga.delta_pct === null || riga.delta_pct === undefined) return '—';
  return `${riga.delta_pct > 0 ? '+' : ''}${fmtNum(riga.delta_pct, 1)}%`;
};

export const deltaSegno = (riga) => {
  const v = riga.tipo === 'percento' ? riga.delta_punti : riga.delta_pct;
  if (v === null || v === undefined || Math.abs(v) < 0.05) return 0;
  return v > 0 ? 1 : -1;
};
